import { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import Select, { SelectOption } from '../../BaseComponents/Select';
import {
  listAiModels,
  sendRestrictedChatMessage,
  RestrictedChatResponse,
} from '../../../api/chat';
import { toast } from 'react-toastify';
import { ReactComponent as ArrowUpIcon } from '../../../assets/icons/arrow-up.svg';
import AiMarkdownContent from '../MemberInfoCard/AiMarkdownContent';
import './MemberRestrictedChat.scss';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

const formatBotResponse = (data: RestrictedChatResponse): string => {
  const text = data?.reply ?? '';
  return typeof text === 'string' ? text.trim() : '';
};

const MemberRestrictedChat = () => {
  const { memberData } = useSelector((state: RootState) => state.member);
  const memberId = memberData?.user_id ?? '';

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [modelOptions, setModelOptions] = useState<SelectOption[]>([]);
  const [selectedModel, setSelectedModel] = useState<SelectOption>({ key: '', value: 'Loading models...' });
  const [defaultModelKey, setDefaultModelKey] = useState('');
  const [isLoadingModels, setIsLoadingModels] = useState(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const fetchModels = async () => {
      setIsLoadingModels(true);
      try {
        const { models, default_model_key, default_model_name } = await listAiModels();
        const activeModels = models.filter((m) => m.active);
        const resolvedDefaultKey = default_model_key?.trim() || '';
        const resolvedDefaultName = default_model_name?.trim() || resolvedDefaultKey;
        setDefaultModelKey(resolvedDefaultKey);

        const defaultOption: SelectOption = {
          key: '',
          value: resolvedDefaultName
            ? `${resolvedDefaultName} (Default)`
            : 'Default (env deployment)',
        };

        const options: SelectOption[] = [
          defaultOption,
          ...activeModels
            .filter((m) => m.key !== resolvedDefaultKey)
            .map((m) => ({
              key: m.key,
              value: m.name,
            })),
        ];

        setModelOptions(options);
        setSelectedModel(defaultOption);
      } catch (err: unknown) {
        const errorMessage =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          'Failed to load AI models';
        toast.error(errorMessage);
        setSelectedModel({ key: '', value: 'Default' });
      } finally {
        setIsLoadingModels(false);
      }
    };

    fetchModels();
  }, []);

  const handleModelSelect = (option: SelectOption) => {
    setSelectedModel(option);
  };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (!trimmed || isLoading || !memberId) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const modelKey =
        selectedModel.key &&
        String(selectedModel.key) !== defaultModelKey
          ? String(selectedModel.key)
          : undefined;
      const data = await sendRestrictedChatMessage(memberId, trimmed, modelKey);
      const responseText = formatBotResponse(data);

      if (!responseText) {
        if (data.success === false) {
          throw new Error('Request was not successful');
        }
        throw new Error('No response text found in API response');
      }

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: responseText,
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err: unknown) {
      let errorMessage = 'Something went wrong. Please try again.';

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'object' && err !== null) {
        const axiosError = err as {
          response?: { data?: { detail?: string; message?: string } };
          message?: string;
        };
        errorMessage =
          axiosError?.response?.data?.detail ||
          axiosError?.response?.data?.message ||
          axiosError?.message ||
          errorMessage;
      }

      toast.error(errorMessage);
      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-error-${Date.now()}`,
          role: 'assistant',
          content: `Error: ${errorMessage}`,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!memberId) {
    return <div className="member-restricted-chat">Member not loaded.</div>;
  }

  return (
    <div className="member-restricted-chat">
      <div className="member-restricted-chat-header sideBarPad2">
        <div className="member-restricted-chat-header-content">
          <div className="member-restricted-chat-header-icon" aria-hidden="true">
            AI
          </div>
          <div>
            <h2 className="member-restricted-chat-title">Unrestricted Chat</h2>
            <p className="member-restricted-chat-subtitle">
              Staff-only chat with full member clinical context — notes, appointments, biomarkers, and scans.
            </p>
          </div>
        </div>
      </div>

      <div className="member-restricted-chat-messages">
        {messages.length === 0 && (
          <div className="member-restricted-chat-placeholder">
            <div className="member-restricted-chat-placeholder-icon" aria-hidden="true">
              ✦
            </div>
            <p className="member-restricted-chat-placeholder-title">Ask about this member</p>
            <p className="member-restricted-chat-placeholder-text">
              Type your question below, pick a model, and press Send. Answers are grounded in this member&apos;s clinical data.
            </p>
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`member-restricted-chat-message member-restricted-chat-message--${msg.role}`}
          >
            <div className="member-restricted-chat-message-avatar" aria-hidden="true">
              {msg.role === 'user' ? 'You' : 'AI'}
            </div>
            <div className="member-restricted-chat-message-bubble">
              <div className="member-restricted-chat-message-content">
                {msg.role === 'assistant' ? (
                  <AiMarkdownContent content={msg.content} />
                ) : (
                  msg.content
                )}
              </div>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="member-restricted-chat-message member-restricted-chat-message--assistant">
            <div className="member-restricted-chat-message-avatar" aria-hidden="true">
              AI
            </div>
            <div className="member-restricted-chat-message-bubble member-restricted-chat-message-bubble--loading">
              <span className="member-restricted-chat-typing-dots">
                <span />
                <span />
                <span />
              </span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="member-restricted-chat-form" onSubmit={handleSubmit}>
        <div className="member-restricted-chat-composer">
          <textarea
            ref={inputRef}
            className="member-restricted-chat-input"
            placeholder="Ask a question about this member's clinical data..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            rows={3}
            disabled={isLoading}
          />
          <div className="member-restricted-chat-composer-footer">
            <div className="member-restricted-chat-model-select">
              <label className="member-restricted-chat-model-label">
                Model
              </label>
              <Select
                name="unrestricted_chat_model"
                options={
                  modelOptions.length > 0
                    ? modelOptions
                    : [{ key: '', value: 'Default' }]
                }
                selectedOption={selectedModel}
                onChange={handleModelSelect}
                isError={false}
                placeholder="Select model"
                isDisable={isLoadingModels || isLoading}
                useMenuPortal
                openMenuToTop
              />
            </div>
            <button
              type="submit"
              className="member-restricted-chat-send"
              disabled={!inputValue.trim() || isLoading}
              aria-label="Send message"
            >
              <ArrowUpIcon className="member-restricted-chat-send-icon" />
              <span>Send</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MemberRestrictedChat;
